import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Page, Card, BlockStack, Text, Badge, DataTable, Modal, TextField, Select, FormLayout, Button, InlineStack, Toast, Box, Layout } from '@shopify/polaris';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import EmployeeTimeline from '../../shared/components/interactions/EmployeeTimeline';

export default function EmployeePage() {
  const { id } = useParams();
  const pagination = useRecordPagination('employees', id, 'employee');
  const [user, setUser] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saldos, setSaldos] = useState([]);
  const [saldoTypes, setSaldoTypes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editingSaldo, setEditingSaldo] = useState(null);
  const [formSaldoType, setFormSaldoType] = useState('');
  const [formSaldo, setFormSaldo] = useState('');
  const [formMemo, setFormMemo] = useState('');
  const [formYear, setFormYear] = useState(new Date().getFullYear().toString());
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  const showToast = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };

  const toggleToast = useCallback(() => setToastActive((active) => !active), []);

  // Generate year options (last 5 years + next year)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) {
    yearOptions.push({ label: y.toString(), value: y.toString() });
  }

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const [uRes, vRes, typesRes] = await Promise.all([fetch(`/api/users/${id}`), fetch(`/api/voertuigen/by-user/${id}`), fetch('/api/users/saldo-types')]);
        const uData = await uRes.json();
        setUser(Array.isArray(uData) ? uData[0] : uData);
        if (vRes.ok) {
          const vData = await vRes.json();
          setVehicle(vData);
        } else {
          setVehicle(null);
        }
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setSaldoTypes(typesData);
        }
      } catch (e) {
        console.error('Failed to fetch employee or vehicle', e);
      }
      setLoading(false);
    }
    if (id) fetchAll();
  }, [id]);

  const fetchSaldos = useCallback(async () => {
    if (!id || !selectedYear) return;
    try {
      const res = await fetch(`/api/users/${id}/saldos/${selectedYear}/latest`);
      if (res.ok) {
        const data = await res.json();
        setSaldos(data);
      }
    } catch (e) {
      console.error('Failed to fetch saldos', e);
    }
  }, [id, selectedYear]);

  useEffect(() => {
    fetchSaldos();
  }, [fetchSaldos]);

  const handleOpenAddModal = () => {
    setModalMode('add');
    setEditingSaldo(null);
    setFormSaldoType(saldoTypes.length > 0 ? saldoTypes[0].VerlofSaldoTypeID.toString() : '');
    setFormSaldo('');
    setFormMemo('');
    setFormYear(selectedYear);
    setModalOpen(true);
  };

  const handleOpenEditModal = (saldo) => {
    setModalMode('edit');
    setEditingSaldo(saldo);
    setFormSaldoType(saldo.VerlofSaldoTypeID.toString());
    setFormSaldo(saldo.Saldo?.toString() || '');
    setFormMemo(saldo.Memo || '');
    setFormYear(selectedYear);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingSaldo(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modalMode === 'add') {
        const res = await fetch(`/api/users/${id}/saldos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            VerlofSaldoTypeID: parseInt(formSaldoType),
            Saldo: parseFloat(formSaldo) || 0,
            Memo: formMemo,
            Year: parseInt(formYear)
          })
        });
        if (res.ok) {
          showToast('Saldo added successfully');
          fetchSaldos();
          handleCloseModal();
        } else {
          const errorData = await res.json().catch(() => ({}));
          showToast(errorData.msg || 'Failed to add saldo', true);
        }
      } else {
        const res = await fetch(`/api/users/${id}/saldos/${editingSaldo.VerlofSaldoHistoriekID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Saldo: parseFloat(formSaldo) || 0,
            Memo: formMemo
          })
        });
        if (res.ok) {
          showToast('Saldo updated successfully');
          fetchSaldos();
          handleCloseModal();
        } else {
          showToast('Failed to update saldo', true);
        }
      }
    } catch (e) {
      console.error('Failed to save saldo', e);
      showToast('Error saving saldo', true);
    }
    setSaving(false);
  };

  const handleDelete = async (saldoId) => {
    if (!confirm('Are you sure you want to delete this saldo entry?')) return;
    try {
      const res = await fetch(`/api/users/${id}/saldos/${saldoId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        showToast('Saldo deleted successfully');
        fetchSaldos();
      } else {
        showToast('Failed to delete saldo', true);
      }
    } catch (e) {
      console.error('Failed to delete saldo', e);
      showToast('Error deleting saldo', true);
    }
  };

  const title = user ? user.Gebruikernaam || user.Gebruiker || `Employee #${id}` : `Employee #${id}`;

  const saldoTypeOptions = saldoTypes.map((t) => ({
    label: t.SaldoType,
    value: t.VerlofSaldoTypeID.toString()
  }));

  const saldoRows = saldos.map((s) => [
    s.SaldoType,
    s.Saldo != null ? s.Saldo.toString() : '0',
    s.Memo || '',
    <InlineStack gap="200">
      <Button size="micro" onClick={() => handleOpenEditModal(s)}>
        Edit
      </Button>
      <Button size="micro" tone="critical" onClick={() => handleDelete(s.VerlofSaldoHistoriekID)}>
        Delete
      </Button>
    </InlineStack>
  ]);

  return (
    <Page pagination={pagination} title={title} fullWidth>
      {toastActive && <Toast content={toastMessage} error={toastError} onDismiss={toggleToast} />}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalMode === 'add' ? 'Add Vacation Saldo' : 'Edit Vacation Saldo'}
        primaryAction={{
          content: modalMode === 'add' ? 'Add Saldo' : 'Update Saldo',
          onAction: handleSave,
          loading: saving
        }}
        secondaryActions={[{ content: 'Cancel', onAction: handleCloseModal }]}
      >
        <Modal.Section>
          <FormLayout>
            {modalMode === 'add' && (
              <>
                <Select label="Year" options={yearOptions} value={formYear} onChange={setFormYear} />
                <Select label="Saldo Type" options={saldoTypeOptions} value={formSaldoType} onChange={setFormSaldoType} />
              </>
            )}
            {modalMode === 'edit' && <TextField label="Saldo Type" value={editingSaldo?.SaldoType || ''} disabled />}
            <TextField label="Saldo (hours)" type="number" value={formSaldo} onChange={setFormSaldo} autoComplete="off" />
            <TextField label="Memo" value={formMemo} onChange={setFormMemo} multiline={3} autoComplete="off" />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Layout>
        {/* Left side - Employee Info (smaller) */}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="200">
                <CardHeading title="General" />
                <Text as="p" variant="bodySm">
                  ID: {user?.GebruikerID}
                </Text>
                <Text as="p" variant="bodySm">
                  Username: {user?.Gebruiker}
                </Text>
                <Text as="p" variant="bodySm">
                  Name: {user?.Gebruikernaam}
                </Text>
                <Text as="p" variant="bodySm">
                  Email: {user?.Email}
                </Text>
                <Text as="p" variant="bodySm">
                  Abbreviation: {user?.Afkorting}
                </Text>
                <Text as="p" variant="bodySm">
                  Role: {user?.Gebruikertype || user?.UserType}
                </Text>
                <Text as="p" variant="bodySm">
                  On payroll: {user?.OnPayroll ? 'Yes' : 'No'}
                </Text>
                <Text as="p" variant="bodySm">
                  Employment start: {user?.EmploymentStart ? new Date(user.EmploymentStart).toLocaleDateString() : ''}
                </Text>
                <Text as="p" variant="bodySm">
                  Gross/month: {user?.GrossMonth != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(user.GrossMonth) : ''}
                </Text>
                <Text as="p" variant="bodySm">
                  Status: {user?.Inactief ? <Badge tone="critical">Inactive</Badge> : <Badge tone="success">Active</Badge>}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <CardHeading title="Vehicle" />
                {vehicle ? (
                  <>
                    <Text as="p" variant="bodySm">
                      Brand: {vehicle.Merk}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Model: {vehicle.Model}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Plate: {vehicle.Nummerplaat}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Fuel: {vehicle.Brandstof}
                    </Text>
                    <Text as="p" variant="bodySm">
                      EuroNorm: {vehicle.EuroNorm}
                    </Text>
                    <Text as="p" variant="bodySm">
                      CO2 g/km: {vehicle.CO2GramPerKm}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Start date: {vehicle.DatumStart ? new Date(vehicle.DatumStart).toLocaleDateString() : ''}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Catalog price: {user?.VehicleCatalogusPrijs != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(user.VehicleCatalogusPrijs) : ''}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Monthly cost: {user?.VehicleMonthlyCost != null ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(user.VehicleMonthlyCost) : ''}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Discontinued: {vehicle.Discontinued ? 'Yes' : 'No'}
                    </Text>
                  </>
                ) : (
                  <Text as="p" variant="bodySm" tone="subdued">
                    No active vehicle assigned.
                  </Text>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        {/* Right side - Timeline (larger) */}
        <Layout.Section>
          <EmployeeTimeline employeeId={id} />
        </Layout.Section>

        {/* Full width - Vacation Saldos */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <CardHeading title="Vacation Saldos" />
                <InlineStack gap="200">
                  <Select label="" labelHidden options={yearOptions} value={selectedYear} onChange={setSelectedYear} />
                  <Button onClick={handleOpenAddModal}>Add Saldo</Button>
                </InlineStack>
              </InlineStack>
              {saldos.length === 0 ? (
                <Box paddingBlockStart="200">
                  <Text as="p" tone="subdued">
                    No vacation saldos for {selectedYear}. Click "Add Saldo" to create one.
                  </Text>
                </Box>
              ) : (
                <DataTable columnContentTypes={['text', 'numeric', 'text', 'text']} headings={['Type', 'Saldo (hours)', 'Memo', 'Actions']} rows={saldoRows} increasedTableDensity />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
